import type { TranslationKey } from "@/constants/i18n";

/**
 * The stand-in card a canvasser hands over: eleven digits, nothing else.
 *
 * The rule lived in three places — the enrolment form, the card edit and the
 * API's validator — and two of them only agreed by accident. Here it is one
 * pair of functions, the way legacy's `temporaryCardNumberValidation` had it:
 * one that keeps the field typeable, one that says why a value is refused.
 */
export const TEMPORARY_CARD_DIGITS = 11;

/**
 * What the field is allowed to hold as it is typed.
 *
 * Digits only and never longer than a card number, so a pasted "4271-5954-981"
 * lands as the number it is rather than being refused on Submit.
 */
export function sanitizeTemporaryCard(value: string): string {
  return value.replace(/\D/g, "").slice(0, TEMPORARY_CARD_DIGITS);
}

/**
 * Why the value cannot be saved, as a message key, or nothing if it can.
 *
 * Empty is not an error: the stand-in card is cleared by emptying the field,
 * and the one screen that insists on one — a canvassing enrolment — asks for
 * it itself, as the API does.
 */
export function temporaryCardError(
  value: string | null | undefined,
): TranslationKey | undefined {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return undefined;
  return trimmed.length === TEMPORARY_CARD_DIGITS && /^\d+$/.test(trimmed)
    ? undefined
    : "md.temporaryCardDigits";
}
